// ============================================================
// ruleCoverage - does the engine actually APPLY a catalog entry?
//
// The catalogs describe every talent and trait, but the engine reads
// only some effect types (and a handful of entries by id). Now that any
// entry can be added in a session, the picker has to be honest about
// which ones change a roll and which are a note on the sheet: nobody
// should take Sound Constitution expecting wounds and get none.
//
// This is a statement about the ENGINE, kept beside the catalogs and
// pinned by a snapshot test, so wiring a new effect type shows up as a
// changed tag rather than a stale one.
// ============================================================
import type { TalentEffect } from './talentEffects';

export type RuleCoverage = 'applied' | 'partial' | 'note';

/**
 * Effect types the catalogs use that NO engine code consumes yet. Wiring
 * them is engine work of its own (dev-docs/plans/deferred-actions.md).
 */
const UNAPPLIED: ReadonlySet<TalentEffect['type']> = new Set([
  'wound_bonus',
  'condition_immunity',
  'crit_reduction',
  'threshold_modifier',
  'penetration_bonus',
]);

/**
 * Entries the engine reads BY ID, whatever their effect list says: the
 * resolver's talent and trait flags, the attack flow's flags, the
 * battlefield's movement traits and the stance hook.
 */
const READ_BY_ID: ReadonlySet<string> = new Set([
  // combatStatsResolver: defenceTalents, traitFlags, getTraitParam
  ...['true_grit', 'hardy', 'iron_jaw', 'counter_attack', 'frenzy', 'battle_rage', 'deflect_shot'],
  ...['stuff_of_nightmares', 'astartes', 'from_beyond', 'incorporeal', 'undying', 'machine', 'size', 'toxic_trait'],
  // combatStatsResolver: ATTACKER_TALENT_FLAGS
  ...['berserk_charge', 'hammer_blow', 'blademaster', 'sure_strike', 'hunter_of_aliens', 'scourge_of_heretics'],
  ...['furious_assault', 'deathwatch_training'],
  // battlefield/traitRules and the stance hook
  ...['flyer', 'phase', 'hoverer', 'burrower', 'leap_up'],
]);

/**
 * How much of an entry the engine applies: all of its rules, some of
 * them, or none (a note on the sheet). `narrative` effects are prose and
 * count for nothing either way.
 */
export function ruleCoverage(def: { id: string; effects: TalentEffect[] }): RuleCoverage {
  if (READ_BY_ID.has(def.id)) return 'applied';
  const rules = def.effects.filter((effect) => effect.type !== 'narrative');
  const applied = rules.filter((effect) => !UNAPPLIED.has(effect.type));
  if (applied.length === 0) return 'note';
  return applied.length === rules.length ? 'applied' : 'partial';
}
